import type { MixedItemTag } from "@memorang/types";
import { resolveRecommendationCollectionIds } from "../dashboard-integration";

export type RecommendationFrontendContext = {
	enabled: boolean;
	userId?: number;
	examId: string;
	collectionIds: number[];
	skills: MixedItemTag[];
	proficiencies: MixedItemTag[];
	questionTypes: MixedItemTag[];
	hasPremiumAccess: boolean;
	freeTagIds: number[];
	targetProficiencyId?: number;
	preferredSkillIds: number[];
};

export const buildRecommendationFrontendContext = ({
	userId,
	examId,
	activeStudyPackCollectionId,
	studyPackCollectionId,
	freeStudyPackCollectionId,
	examCollectionIds,
	tags,
	freeTagIds,
	hasPremiumAccess,
	targetLevelId,
	onboardingSkillIds,
}: {
	userId?: number;
	examId: string;
	activeStudyPackCollectionId?: number;
	studyPackCollectionId?: number;
	freeStudyPackCollectionId?: number;
	examCollectionIds?: number[];
	tags: MixedItemTag[];
	freeTagIds: number[];
	hasPremiumAccess: boolean;
	targetLevelId?: string;
	onboardingSkillIds?: string[];
}): RecommendationFrontendContext => {
	const freeTagIdSet = new Set(freeTagIds);
	const normalizedTags = tags.map((tag) => ({
		...tag,
		locked: !(hasPremiumAccess || freeTagIdSet.has(tag.id)),
	}));
	const skills = normalizedTags.filter((tag) => tag.type === "skill");
	const proficiencies = normalizedTags
		.filter((tag) => tag.type === "Proficiency")
		.sort(
			(left, right) =>
				(left.sortOrder ?? 0) - (right.sortOrder ?? 0) || left.id - right.id,
		);
	const questionTypes = normalizedTags.filter(
		(tag) => tag.type === "Question Type",
	);
	const target = targetLevelId?.toLowerCase();
	const targetProficiency =
		proficiencies.find(
			(tag) =>
				tag.title.toLowerCase() === target ||
				tag.code?.toLowerCase() === target ||
				String(tag.id) === target,
		) ?? proficiencies[0];
	const preferredNames = new Set(
		(onboardingSkillIds ?? []).map((id) => id.toLowerCase()),
	);
	const preferredSkillIds = skills
		.filter(
			(tag) =>
				preferredNames.has(tag.title.toLowerCase()) ||
				(tag.code ? preferredNames.has(tag.code.toLowerCase()) : false) ||
				preferredNames.has(String(tag.id)),
		)
		.map(({ id }) => id);

	return {
		enabled: true,
		userId,
		examId,
		collectionIds: resolveRecommendationCollectionIds({
			activeStudyPackCollectionId,
			studyPackCollectionId,
			freeStudyPackCollectionId,
			examCollectionIds,
		}),
		skills,
		proficiencies,
		questionTypes,
		hasPremiumAccess,
		freeTagIds,
		targetProficiencyId: targetProficiency?.id,
		preferredSkillIds,
	};
};
